'use client';

import { useCallback, useSyncExternalStore } from 'react';
import { getBrowserWindow } from '@/lib/utils/browser-window';

export type SummarizerStreamingEffect = 'none' | 'typewriter';

const STORAGE_KEY = 'summarizer-streaming-effect';
const DEFAULT_EFFECT: SummarizerStreamingEffect = 'none';

let currentEffect: SummarizerStreamingEffect = DEFAULT_EFFECT;
const listeners = new Set<() => void>();

const isValidEffect = (value: unknown): value is SummarizerStreamingEffect =>
  value === 'none' || value === 'typewriter';

const readEffectFromStorage = (): SummarizerStreamingEffect => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) {
    return currentEffect;
  }

  try {
    const stored = browserWindow.localStorage.getItem(STORAGE_KEY);
    if (isValidEffect(stored)) {
      currentEffect = stored;
    } else if (stored !== null) {
      currentEffect = DEFAULT_EFFECT;
      browserWindow.localStorage.setItem(STORAGE_KEY, currentEffect);
    }
  } catch {
    currentEffect = DEFAULT_EFFECT;
  }

  return currentEffect;
};

const emit = () => {
  for (const listener of listeners) {
    listener();
  }
};

const setEffectInternal = (effect: SummarizerStreamingEffect) => {
  if (effect === currentEffect) {
    return;
  }
  currentEffect = effect;
  const browserWindow = getBrowserWindow();
  if (browserWindow) {
    try {
      browserWindow.localStorage.setItem(STORAGE_KEY, effect);
    } catch {
      // Ignore storage write errors (e.g., private mode)
    }
  }
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  const browserWindow = getBrowserWindow();
  if (browserWindow) {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) {
        return;
      }
      if (isValidEffect(event.newValue)) {
        currentEffect = event.newValue;
        emit();
      } else if (event.newValue === null) {
        currentEffect = DEFAULT_EFFECT;
        emit();
      }
    };

    browserWindow.addEventListener('storage', handleStorage);
    return () => {
      listeners.delete(listener);
      browserWindow.removeEventListener('storage', handleStorage);
    };
  }

  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) {
    return currentEffect;
  }
  return readEffectFromStorage();
};

const getServerSnapshot = () => currentEffect;

export function useSummarizerStreamingEffect(): [
  SummarizerStreamingEffect,
  (effect: SummarizerStreamingEffect) => void,
] {
  const effect = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setEffect = useCallback((next: SummarizerStreamingEffect) => {
    setEffectInternal(next);
  }, []);

  return [effect, setEffect];
}
